// SmartDateRangeField.js
'use client';
import * as React from 'react';
import { Typography } from '@mui/material';
import { DateRangePicker } from 'rsuite';
// import 'rsuite/dist/rsuite-no-reset.min.css';

const pad = (n) => String(n).padStart(2, '0');
const toLocalDateTime = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toLocalDate = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseLocalDateTime = (s) => {
    if (!s) return null;
    const [datePart, timePartRaw] = String(s).trim().replace(' ', 'T').split('T');
    if (!datePart) return null;
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh = '0', mm = '0'] = (timePartRaw || '').split(':');
    if ([y, m, d].some(Number.isNaN)) return null;
    return new Date(y, (m || 1) - 1, d || 1, Number(hh) || 0, Number(mm) || 0, 0, 0);
};
const parseLocalDateOnly = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).split('-').map(Number);
    if ([y, m, d].some(Number.isNaN)) return null;
    return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
};
const roundToStep = (d, minutes = 15, mode = 'ceil') => {
    if (minutes <= 1) return d;
    const q = d.getMinutes() / minutes;
    const r = mode === 'ceil' ? Math.ceil(q) * minutes : mode === 'floor' ? Math.floor(q) * minutes : Math.round(q) * minutes;
    d.setMinutes(r, 0, 0);
    return d;
};
const clampDate = (d, min, max) => (min && d < min) ? new Date(min) : (max && d > max) ? new Date(max) : d;

export default function SmartDateRangeField({
    type = 'datetime',
    value,
    setValue,
    minDateTime,
    maxDateTime,
    minDate,
    maxDate,
    stepMinutes = 15,
    rounding = 'ceil',
    placeholder = 'Select range',
    showHint = true,
    sx,
    container = () => (typeof document !== 'undefined' ? document.body : null),
    ...rest
}) {
    const minD = type === 'datetime' ? parseLocalDateTime(minDateTime) : parseLocalDateOnly(minDate);
    const maxD = type === 'datetime' ? parseLocalDateTime(maxDateTime) : parseLocalDateOnly(maxDate);

    const toDates = React.useCallback((tuple) => {
        if (!tuple || !Array.isArray(tuple)) return [null, null];
        const [a, b] = tuple;
        return type === 'datetime' ? [parseLocalDateTime(a), parseLocalDateTime(b)]
            : [parseLocalDateOnly(a), parseLocalDateOnly(b)];
    }, [type]);
    const toStrings = React.useCallback(([s, e]) =>
        type === 'datetime'
            ? [s ? toLocalDateTime(s) : '', e ? toLocalDateTime(e) : '']
            : [s ? toLocalDate(s) : '', e ? toLocalDate(e) : '']
        , [type]);

    const dates = toDates(value);
    // ✅ keep partial selection so user can pick the end date
    const rsuiteValue = (dates[0] || dates[1]) ? dates : null;

    const belowMin = (dates[0] && minD && dates[0] < minD) || (dates[1] && minD && dates[1] < minD);
    const aboveMax = (dates[0] && maxD && dates[0] > maxD) || (dates[1] && maxD && dates[1] > maxD);

    const normalizeRange = (picked) => {
        if (!picked) return setValue(['', '']);
        let [s, e] = picked;
        if (s) { if (type === 'datetime') s = roundToStep(new Date(s), stepMinutes, rounding); s = clampDate(s, minD, maxD); }
        if (e) { if (type === 'datetime') e = roundToStep(new Date(e), stepMinutes, rounding); e = clampDate(e, minD, maxD); }
        if (s && e && s > e) [s, e] = [e, s];
        setValue(toStrings([s || null, e || null]));
    };

    return (
        <div style={{ width: '100%', ...(sx || {}) }}>
            <DateRangePicker
                value={rsuiteValue}
                onChange={(next) => normalizeRange(next || null)}
                format={type === 'datetime' ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd'}
                placeholder={placeholder}
                showHeader
                container={container}
                disabledDate={(date) => {
                    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                    if (minD) {
                        const md = new Date(minD.getFullYear(), minD.getMonth(), minD.getDate(), 0, 0, 0, 0);
                        if (day < md) return true;
                    }
                    if (maxD) {
                        const xd = new Date(maxD.getFullYear(), maxD.getMonth(), maxD.getDate(), 0, 0, 0, 0);
                        if (day > xd) return true;
                    }
                    return false;
                }}
                {...rest}
            />
            {showHint && (belowMin || aboveMax) && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {type === 'datetime'
                        ? <>
                            {belowMin && minDateTime ? `Minimum start/end: ${String(minDateTime).replace('T', ' ')}` : null}
                            {aboveMax && maxDateTime ? `Maximum start/end: ${String(maxDateTime).replace('T', ' ')}` : null}
                        </>
                        : <>
                            {belowMin && minDate ? `Earliest allowed: ${minDate}` : null}
                            {aboveMax && maxDate ? `Latest allowed: ${maxDate}` : null}
                        </>
                    }
                </Typography>
            )}
        </div>
    );
}
